import {
  Badge,
  Button,
  Collapse,
  Input,
  InputGroup,
  InputGroupAddon,
  Progress,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Table,
  Container,
  Row,
  CardHeader,
  Card,
  CardFooter,
  Pagination,
  PaginationItem,
  PaginationLink,
  Col,
} from 'reactstrap';
import { useState, useEffect, useCallback } from 'react';

import TimelineHeader from '../../components/headers/TimelineHeader';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import { getPropertiesEndpoint } from './services/propertyService';
import { fetchResponseAdapter } from '../../adapters/fetchAdapter';
import { PropertiesList } from './models/propertyModel';
import { Paginator } from '../../models/formModel';
import {
  getOptionPropertyStatus,
  getPropertySaleStatus,
  getPropertyStatus,
  getPublicPropertyName,
  getStatusBadgeColor,
} from './adapters/propertyAdapter';
import useAuth from '../../hooks/useAuth';
import GridSearchBar from '../../components/searchBar/GridSearchBar';
import { useSelector, useDispatch } from 'react-redux';
import { AppStore } from '../../redux/store';
import {
  clearGridFiltersAction,
  setGridFiltersAction,
  setUniqueGridFiltersAction,
} from '../../redux/features/gridSearchBar';
import LoadingCard from '../../components/LoadingCard';
import { generatePagination } from '../../adapters/paginateListAdapter';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getLandlordByEmailEndpoint,
  getLandlordByNameEndpoint,
} from '../accounting/services/accountingService';
import PropertyMobileListing from './components/PropertyMobileListing';
import useUserCountry from '../../hooks/useUserCountry';

const ROWS_PER_PAGE_OPTIONS = [15, 30, 50];
const DEFAULT_ROWS_PER_PAGE = 15;
const COVER_FALLBACK =
  'https://storage.googleapis.com/assets-us-east4-propilatam-dev/properties/default/default_1.jpg';

// "$0.00" o vacío no es un precio: se muestra "—".
const isEmptyAmount = (value?: string | number | null) =>
  value === null ||
  value === undefined ||
  value === '' ||
  Number(String(value).replace(/[^0-9.-]/g, '')) === 0;

const Amount = ({ value }: { value?: string | number | null }) =>
  isEmptyAmount(value) ? (
    <span className="text-muted">—</span>
  ) : (
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
  );

const progressColor = (progress: number) =>
  progress >= 100 ? 'success' : progress < 40 ? 'danger' : 'warning';

const Properties = () => {
  const { getAuthToken, isPermissionAvailable } = useAuth();
  const token = getAuthToken();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const [properties, setProperties] = useState<PropertiesList[]>([]);
  const [paginator, setPaginator] = useState<Paginator>({
    page: 0,
    take: 0,
    itemCount: 0,
    pageCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [paginationList, setPaginationList] = useState(Array<number>);
  const gridSearchBar = useSelector((store: AppStore) => store.gridSearchBar);
  const { getCurrentCountry } = useUserCountry();
  const currentUserCountryCode =
    getCurrentCountry()?.code.toLowerCase() ?? 'sv';

  const loadProperties = async () => {
    const { status, data } = await callEndpoint(
      getPropertiesEndpoint(gridSearchBar.query, token),
    );
    if (status === 200) {
      const response = fetchResponseAdapter(data);
      setProperties(response.data.items || []);
      setPaginator(response.data.meta);
      setPaginationList(
        generatePagination(
          response.data.meta.page,
          response.data.meta.pageCount,
          5,
        ),
      );
    }
  };

  const rowsPerPage =
    Number(gridSearchBar.params.take) || DEFAULT_ROWS_PER_PAGE;
  const [searchText, setSearchText] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Búsqueda libre: número = código, con "@" = correo, otro texto = nombre del
  // propietario (el API filtra por esos campos, no por nombre de propiedad).
  const applySearch = () => {
    const text = searchText.trim();
    if (!text) return clearSearch();
    const filter = /^\d+$/.test(text)
      ? { code: text }
      : text.includes('@')
        ? { landlord_email: text }
        : { landlord_name: text };
    dispatch(setUniqueGridFiltersAction({ ...filter, take: rowsPerPage }));
  };

  const clearSearch = () => {
    setSearchText('');
    dispatch(clearGridFiltersAction({}));
  };

  const goToPage = (page: number) =>
    dispatch(setGridFiltersAction({ page, take: rowsPerPage }));

  // Los filtros viven en la URL (?code=…&page=2) para que sobrevivan a entrar al
  // detalle y volver, y se puedan compartir. Al montar se restauran en redux.
  const [urlParams, setUrlParams] = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    const fromUrl = Object.fromEntries(urlParams.entries());
    if (Object.keys(fromUrl).length > 0) {
      dispatch(setUniqueGridFiltersAction(fromUrl));
      setSearchText(
        fromUrl.code ?? fromUrl.landlord_email ?? fromUrl.landlord_name ?? '',
      );
    }
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;
    const next = new URLSearchParams(
      gridSearchBar.params as Record<string, string>,
    );
    if (next.toString() !== urlParams.toString()) {
      setUrlParams(next, { replace: true });
    }
  }, [gridSearchBar.params, isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;
    loadProperties().catch((e) => console.error(e));
  }, [gridSearchBar, isHydrated]);

  if (!properties && isLoading) {
    return (
      <LoadingCard name={'Lista de propiedades'} parent_name={'Propiedades'} />
    );
  }

  const propertyFilters = [
    {
      label: 'Código de propiedad',
      value: 'code',
      apiParamName: 'code',
      inputType: 'number',
      isUnique: true,
      placeholder: 'Ingresa el código de propiedad',
    },
    {
      label: 'Estado de propiedad',
      value: 'status',
      apiParamName: 'status',
      inputType: 'select',
      inputTypeValues: getOptionPropertyStatus(),
      isUnique: false,
      placeholder: 'Seleccione...',
    },
    {
      label: 'Nombre propietario',
      value: 'landlord_name',
      apiParamName: 'landlord_id',
      inputType: 'search',
      isUnique: true,
      placeholder: 'Ingrese el nombre del propietario',
      inputTypeChoices: async (searchTerm: string) => {
        if (!searchTerm) return [];

        const { status, data } = await callEndpoint(
          getLandlordByNameEndpoint(searchTerm, token),
        );

        if (status === 200) {
          const response = fetchResponseAdapter(data);
          return response.data.map((landlord: any) => ({
            value: landlord.landlord_id,
            label: (
              <>
                <span>{landlord.name}</span>
                <small className="text-gray mx-2">{landlord.phone}</small>
              </>
            ),
          }));
        } else {
          return [];
        }
      },
    },
    {
      label: 'Email propietario',
      value: 'landlord_email',
      apiParamName: 'landlord_id',
      inputType: 'search',
      isUnique: true,
      placeholder: 'Ingrese el email del propietario',
      inputTypeChoices: async (searchTerm: string) => {
        if (!searchTerm) return [];

        const { status, data } = await callEndpoint(
          getLandlordByEmailEndpoint(searchTerm, token),
        );

        if (status === 200) {
          const response = fetchResponseAdapter(data);
          return response.data.map((landlord: any) => ({
            value: landlord.landlord_id,
            label: (
              <>
                <span>{landlord.name}</span>
                <small className="text-gray mx-2">{landlord.phone}</small>
              </>
            ),
          }));
        } else {
          return [];
        }
      },
    },
  ];

  const mobileViewComponent = (
    <Row className="mx-2 d-lg-none">
      {properties.length > 0 &&
        properties.map((property) => (
          <Col md="4" sm="6">
            <PropertyMobileListing property={property} />
          </Col>
        ))}
    </Row>
  );

  const rentMarketplaceUrl = (property: PropertiesList) =>
    `${process.env.REACT_APP_PROPI_WEB}/${currentUserCountryCode}/alquiler/${property.url_detail}/${property.code}`;
  const saleMarketplaceUrl = (property: PropertiesList) =>
    `${process.env.REACT_APP_PROPI_WEB}/${currentUserCountryCode}/venta/${property.url_sale_detail}/${property.code}`;

  const desktopViewComponent = (
    <div className="d-none d-lg-block">
      <Table className="align-items-center table-flush" hover responsive>
        <thead className="thead-light">
          <tr>
            <th scope="col">Propiedad</th>
            <th scope="col">Propietario</th>
            <th scope="col">Renta</th>
            <th scope="col">Venta</th>
            <th scope="col" className="text-center">
              Ofertas
            </th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {properties.map((property) => {
            const hasRentLink =
              property.url_sale_detail && property.status === 'published';
            const hasSaleLink =
              property.url_sale_detail && property.sale_status === 'published';
            const landlordName =
              [
                property.landlordInfo?.first_name,
                property.landlordInfo?.last_name,
              ]
                .filter(Boolean)
                .join(' ') || property.landlordInfo?.name;
            // Nombre como lo ve el cliente en la web (tipo + ubicación asignada)
            const publicName = getPublicPropertyName(property);
            return (
              <tr
                key={property.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/properties/admin/${property.id}`)}
              >
                <th scope="row">
                  <div className="d-flex align-items-center">
                    <img
                      alt=""
                      width={64}
                      height={48}
                      className="rounded mr-3"
                      style={{ objectFit: 'cover' }}
                      src={
                        property.cover && !property.cover.endsWith('null')
                          ? property.cover
                          : COVER_FALLBACK
                      }
                    />
                    <div style={{ minWidth: 170 }}>
                      <span className="text-muted mr-2">#{property.code}</span>
                      {publicName ? (
                        <span className="font-weight-bold">{publicName}</span>
                      ) : (
                        <Badge color="warning">Sin ubicación asignada</Badge>
                      )}
                      <small className="text-muted d-block text-wrap">
                        {property.address}
                      </small>
                      <div className="d-flex align-items-center mt-1">
                        <Progress
                          value={property.admin_data_progress}
                          color={progressColor(property.admin_data_progress)}
                          style={{ width: 70, height: 5 }}
                          className="mb-0 mr-2"
                        />
                        <small className="text-muted">
                          {property.admin_data_progress}%
                        </small>
                      </div>
                    </div>
                  </div>
                </th>
                <td>
                  {landlordName ? (
                    <>
                      <span className="d-block">{landlordName}</span>
                      {property.landlordInfo?.email && (
                        <small className="text-muted">
                          {property.landlordInfo.email}
                        </small>
                      )}
                    </>
                  ) : (
                    <span className="text-muted">Sin propietario</span>
                  )}
                </td>
                <td>
                  <Badge color={getStatusBadgeColor(property.status)}>
                    {property.status
                      ? getPropertyStatus(property.status)
                      : 'No definido'}
                  </Badge>
                  <div className="mt-1">
                    <Amount value={property.monthly_price} />
                  </div>
                </td>
                <td>
                  <Badge color={getStatusBadgeColor(property.sale_status)}>
                    {getPropertySaleStatus(property.sale_status)}
                  </Badge>
                  <div className="mt-1">
                    <Amount value={property.sale_monthly_price} />
                  </div>
                </td>
                <td className="text-center">
                  {property.offers > 0 ? (
                    <Badge color="primary" pill>
                      {property.offers}
                    </Badge>
                  ) : (
                    <span className="text-muted">0</span>
                  )}
                </td>
                <td
                  className="text-right text-nowrap"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    className="btn-icon-only"
                    title="Administrar"
                    aria-label="Administrar propiedad"
                    onClick={() => navigate(`/properties/admin/${property.id}`)}
                  >
                    <i className="fa-solid fa-gear" />
                  </Button>
                  <UncontrolledDropdown className="d-inline-block ml-1">
                    <DropdownToggle
                      className="btn-icon-only text-light"
                      role="button"
                      size="sm"
                      color=""
                      aria-label="Más acciones"
                    >
                      <i className="fas fa-ellipsis-v" />
                    </DropdownToggle>
                    <DropdownMenu
                      className="dropdown-menu-arrow"
                      right
                      container="body"
                      positionFixed
                    >
                      <DropdownItem
                        href={`/properties/admin/${property.id}/rent`}
                      >
                        <i className="fa-solid fa-key" /> Configurar información
                        de renta
                      </DropdownItem>
                      <DropdownItem
                        href={`/properties/admin/${property.id}/sales`}
                      >
                        <i className="fa-solid fa-tag" /> Configurar información
                        de venta
                      </DropdownItem>
                      {hasRentLink && (
                        <DropdownItem
                          href={rentMarketplaceUrl(property)}
                          target="_blank"
                        >
                          <i className="fa-solid fa-arrow-up-right-from-square" />{' '}
                          Ver en marketplace (renta)
                        </DropdownItem>
                      )}
                      {hasSaleLink && (
                        <DropdownItem
                          href={saleMarketplaceUrl(property)}
                          target="_blank"
                        >
                          <i className="fa-solid fa-arrow-up-right-from-square" />{' '}
                          Ver en marketplace (venta)
                        </DropdownItem>
                      )}
                      {isPermissionAvailable('SHOW_MARKETPLACE_SYNC') && (
                        <DropdownItem
                          href={`sync-marketplace/${property.id}/property`}
                        >
                          <i className="fa-solid fa-rotate" /> Sincronizar con
                          marketplace
                        </DropdownItem>
                      )}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </td>
              </tr>
            );
          })}
          {properties.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center text-muted py-5">
                No hay propiedades que coincidan con la búsqueda.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );

  const firstShown = paginator.itemCount
    ? (paginator.page - 1) * paginator.take + 1
    : 0;
  const lastShown = paginator.itemCount
    ? firstShown + properties.length - 1
    : 0;

  return (
    <>
      <TimelineHeader name="Lista de propiedades" parentName="Propiedades" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader className="border-0">
                <Row className="align-items-end">
                  <Col lg="6" md="8" sm="12">
                    <label
                      className="form-control-label"
                      htmlFor="quick-search"
                    >
                      Búsqueda
                    </label>
                    <InputGroup className="mb-3">
                      <Input
                        id="quick-search"
                        type="search"
                        placeholder="Código, nombre o correo del propietario"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') applySearch();
                        }}
                      />
                      <InputGroupAddon addonType="append">
                        <Button color="primary" onClick={applySearch}>
                          Buscar
                        </Button>
                        <Button
                          outline
                          color="secondary"
                          onClick={clearSearch}
                          aria-label="Limpiar búsqueda"
                        >
                          <i className="fa-solid fa-xmark" />
                        </Button>
                      </InputGroupAddon>
                    </InputGroup>
                  </Col>
                  <Col lg="6" md="4" sm="12" className="text-md-right">
                    <Button
                      color="link"
                      className="mb-3 px-0"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                      <i className="fa-solid fa-sliders mr-1" />
                      Más filtros
                    </Button>
                  </Col>
                </Row>
                <Collapse isOpen={showAdvanced}>
                  <GridSearchBar filtersList={propertyFilters} />
                </Collapse>
              </CardHeader>
              {desktopViewComponent}
              {mobileViewComponent}
              <CardFooter className="py-4">
                <Row className="align-items-center">
                  <Col md="5" className="mb-3 mb-md-0">
                    <small className="text-muted">
                      Mostrando {firstShown}-{lastShown} de{' '}
                      {paginator.itemCount} propiedades
                    </small>
                    <span className="mx-3 text-muted">|</span>
                    <small className="text-muted mr-2">Filas</small>
                    <Input
                      type="select"
                      bsSize="sm"
                      aria-label="Filas por página"
                      className="d-inline-block w-auto"
                      value={rowsPerPage}
                      onChange={(e) =>
                        dispatch(
                          setGridFiltersAction({
                            take: Number(e.target.value),
                            page: 1,
                          }),
                        )
                      }
                    >
                      {ROWS_PER_PAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md="7">
                    <nav aria-label="Paginación">
                      <Pagination
                        className="pagination justify-content-end mb-0"
                        listClassName="justify-content-end mb-0"
                      >
                        <PaginationItem
                          className={
                            !paginator.hasPreviousPage ? 'disabled' : ''
                          }
                        >
                          <PaginationLink onClick={() => goToPage(1)}>
                            <i className="fas fa-angle-double-left" />
                            <span className="sr-only">Primera</span>
                          </PaginationLink>
                        </PaginationItem>
                        <PaginationItem
                          className={
                            !paginator.hasPreviousPage ? 'disabled' : ''
                          }
                        >
                          <PaginationLink
                            onClick={() => goToPage(paginator.page - 1)}
                          >
                            <i className="fas fa-angle-left" />
                            <span className="sr-only">Anterior</span>
                          </PaginationLink>
                        </PaginationItem>
                        {paginationList.map((page) => (
                          <PaginationItem
                            key={page}
                            className={paginator.page === page ? 'active' : ''}
                          >
                            <PaginationLink onClick={() => goToPage(page)}>
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem
                          className={!paginator.hasNextPage ? 'disabled' : ''}
                        >
                          <PaginationLink
                            onClick={() => goToPage(paginator.page + 1)}
                          >
                            <i className="fas fa-angle-right" />
                            <span className="sr-only">Siguiente</span>
                          </PaginationLink>
                        </PaginationItem>
                        <PaginationItem
                          className={!paginator.hasNextPage ? 'disabled' : ''}
                        >
                          <PaginationLink
                            onClick={() => goToPage(paginator.pageCount)}
                          >
                            <i className="fas fa-angle-double-right" />
                            <span className="sr-only">Última</span>
                          </PaginationLink>
                        </PaginationItem>
                      </Pagination>
                    </nav>
                  </Col>
                </Row>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default Properties;
